// RegistroStreaming.js — Formulario «Crear cuenta» de la página /register en
// los proyectos con portada streaming (Learning). Pide los mismos datos que el
// registro anterior de WordPress: nombre, apellido, documento, teléfono, país
// (y ciudad si es Colombia), institución, profesión (y especialidad si es
// Medicina), correo, contraseña y la aceptación de términos.
//
// Va a auth.php?action=register: nombre completo, dni, telefono + codigo_pais,
// pais, especialidad y el resto en campos_personalizados (apellido, ciudad,
// institución, profesión y la aceptación con su fecha). Las listas se pueden
// cambiar desde el admin (config.listas).
import React, { useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

export const PAISES_DEF = [
  ['Argentina', '+54'], ['Bolivia', '+591'], ['Brasil', '+55'], ['Chile', '+56'], ['Colombia', '+57'], ['Costa Rica', '+506'],
  ['Cuba', '+53'], ['Ecuador', '+593'], ['El Salvador', '+503'], ['Guatemala', '+502'], ['Honduras', '+504'], ['México', '+52'],
  ['Nicaragua', '+505'], ['Panamá', '+507'], ['Paraguay', '+595'], ['Perú', '+51'], ['República Dominicana', '+1'], ['Uruguay', '+598'],
  ['Venezuela', '+58'], ['EE.UU', '+1'], ['España', '+34'],
];
export const LISTAS_DEF = {
  ciudades: ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga', 'Cúcuta', 'Villavicencio', 'Santa Marta', 'Pereira',
    'Manizales', 'Ibagué', 'Pasto', 'Neiva', 'Montería', 'Valledupar', 'Popayán', 'Armenia', 'Tunja', 'Quibdó'],
  instituciones: ['Sin institución', 'Cliente', 'Elias', 'Marta', 'Cobos-Violencia Sexual', 'Cafam', 'Cliente - Cobos', 'Menntun',
    'Parto humanizado', 'Cendiatra', 'Diplomado', 'LAAC academy'],
  profesiones: ['Medicina', 'Enfermería', 'Farmacia', 'Odontología', 'Fisioterapia', 'Psicología', 'Otorrinolaringología', 'SST', 'Otro'],
  especialidades: ['Alergología', 'Cardiología', 'Dermatología', 'Endocrinología', 'Gastroenterología', 'Geriatría', 'Ginecología y obstetricia',
    'Hematología', 'Medicina alternativa y terapias complementarias', 'Medicina del deporte', 'Medicina del dolor y cuidados paliativos',
    'Medicina estética', 'Medicina familiar', 'Medicina general', 'Medicina interna', 'Neurología', 'Neumología', 'Oftalmología', 'Ortopedia',
    'Otorrinolaringología', 'Pediatría', 'Psiquiatría', 'Reumatología', 'Urología', 'Otra'],
};

const aparecer = keyframes`from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; }`;
const girar = keyframes`to { transform: rotate(360deg); }`;

const Form = styled.form`
  color: #eef2f7;
  h2 { margin: 0; font-size: 1.7rem; font-weight: 800; font-stretch: 85%; color: #fff; line-height: 1.1; }
  > p { margin: .5rem 0 1.4rem; font-size: .92rem; line-height: 1.5; color: #9aa6b8; }
  fieldset { border: 0; margin: 0 0 1.3rem; padding: 0; min-width: 0; }
  legend { display: flex; align-items: center; gap: .6rem; width: 100%; padding: 0; margin-bottom: .9rem; font-size: .95rem; font-weight: 700; color: #fff; }
  legend::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,.08); }
  legend b { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 50%; font-size: .78rem; background: var(--acento); color: #fff; }
`;
export const Grilla = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: .9rem; align-items: start;
  .ancho { grid-column: 1 / -1; }
  .aparece { animation: ${aparecer} .25s ease-out; }
  @media (max-width: 520px) { grid-template-columns: 1fr; }
`;
export const Campo = styled.label`
  display: block; min-width: 0;
  > span { display: block; margin-bottom: 6px; font-size: .8rem; font-weight: 500; color: #b7c1cf; }
  > span i { font-style: normal; color: var(--acento); }
  .caja { display: flex; align-items: center; height: 48px; border-radius: 9px; background: rgba(255,255,255,.045);
    border: 1px solid ${p => (p.$error ? '#f07a7a' : 'rgba(255,255,255,.11)')}; transition: border-color .15s, box-shadow .15s, background .15s; }
  .caja:focus-within { border-color: var(--acento); background: rgba(255,255,255,.07); box-shadow: 0 0 0 3px color-mix(in srgb, var(--acento) 22%, transparent); }
  input, select { flex: 1; min-width: 0; height: 100%; padding: 0 12px; border: 0; outline: 0; background: transparent; color: #fff; font: inherit; font-size: .95rem; }
  input::placeholder { color: #66717f; }
  select { appearance: none; cursor: pointer; padding-right: 34px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%23aab4c2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m4 6 4 4 4-4'/%3E%3C/svg%3E") no-repeat right 12px center; }
  select:invalid, select option[value=''] { color: #66717f; }
  option { background: #1b1b1b; color: #fff; }
  .prefijo { flex: none; padding-left: 12px; font-size: .95rem; color: #9aa6b8; font-variant-numeric: tabular-nums; }
  .prefijo + input { padding-left: 8px; }
  .ojo { flex: none; display: grid; place-items: center; width: 40px; height: 40px; margin-right: 4px; border: 0; border-radius: 7px; background: none; color: #9aa6b8; cursor: pointer; }
  .ojo:hover { color: #fff; }
  .ojo:focus-visible { outline: 2px solid var(--acento); }
  .ojo svg { width: 19px; height: 19px; }
  small { display: block; margin-top: 5px; font-size: .78rem; color: ${p => (p.$error ? '#ffb3b3' : '#7d8796')}; }
`;
export const Fuerza = styled.div`
  display: flex; gap: 4px; margin-top: 7px;
  i { flex: 1; height: 4px; border-radius: 2px; background: rgba(255,255,255,.1); transition: background .2s; }
  i.on { background: ${p => (p.$nivel >= 3 ? '#3ddc84' : p.$nivel === 2 ? '#f5c46b' : '#f07a7a')}; }
`;
const Acepto = styled.label`
  display: flex; gap: .7rem; align-items: flex-start; padding: .85rem .9rem; border-radius: 10px; cursor: pointer;
  background: rgba(255,255,255,.035); box-shadow: inset 0 0 0 1px ${p => (p.$error ? '#f07a7a' : 'rgba(255,255,255,.08)')};
  font-size: .86rem; line-height: 1.45; color: #c3ccd9;
  input { flex: none; width: 20px; height: 20px; margin: 1px 0 0; accent-color: var(--acento); cursor: pointer; }
  a { color: #fff; text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 3px; }
`;
const Error = styled.p`margin: 0 0 1rem; padding: .7rem .9rem; border-radius: 9px; background: rgba(240,122,122,.1); color: #ffb3b3; font-size: .88rem; line-height: 1.4;
  a { color: #fff; font-weight: 700; }`;
const Enviar = styled.button`
  display: flex; align-items: center; justify-content: center; gap: .6rem; width: 100%; height: 52px; margin-top: 1.1rem; border: 0; border-radius: 9px;
  background: var(--acento); color: #fff; font: inherit; font-size: 1.02rem; font-weight: 700; cursor: pointer; box-shadow: 0 14px 30px -14px var(--acento);
  transition: filter .15s, transform .1s;
  &:hover:not(:disabled) { filter: brightness(1.08); }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: .65; cursor: progress; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
  .giro { width: 16px; height: 16px; border-radius: 50%; border: 2px solid currentColor; border-right-color: transparent; animation: ${girar} .7s linear infinite; }
`;
const Pie = styled.p`margin: 1.2rem 0 0; padding-top: 1rem; border-top: 1px solid rgba(255,255,255,.07); text-align: center; font-size: .9rem; color: #9aa6b8;
  a { color: var(--acento); font-weight: 700; text-decoration: none; } a:hover { text-decoration: underline; }`;
const Listo = styled.div`
  text-align: center; padding: 2rem 0;
  .check { display: grid; place-items: center; width: 76px; height: 76px; margin: 0 auto 1.1rem; border-radius: 50%; background: var(--acento); color: #fff;
    box-shadow: 0 16px 40px -14px var(--acento); }
  .check svg { width: 36px; height: 36px; }
  h2 { margin: 0 0 .4rem; font-size: 1.6rem; font-weight: 800; font-stretch: 85%; color: #fff; }
  p { margin: 0; color: #9aa6b8; }
`;

export const Ojo = ({ tachado }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="3" />{tachado && <path d="M4 4l16 16" />}
  </svg>
);
export const nivelClave = (c) => (!c ? 0 : c.length < 6 ? 1 : (/[A-Z]/.test(c) + /[0-9]/.test(c) + /[^A-Za-z0-9]/.test(c) + (c.length >= 10)) >= 2 ? 3 : 2);

const RegistroStreaming = ({ config = {}, destino = '/' }) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const listas = useMemo(() => {
    const l = config.listas || {};
    const de = (k) => (Array.isArray(l[k]) && l[k].filter(Boolean).length ? l[k].filter(Boolean) : LISTAS_DEF[k]);
    return { ciudades: de('ciudades'), instituciones: de('instituciones'), profesiones: de('profesiones'), especialidades: de('especialidades') };
  }, [config.listas]);
  const [f, setF] = useState({ nombre: '', apellido: '', documento: '', telefono: '', pais: '', ciudad: '', institucion: '', profesion: '', especialidad: '', email: '', clave: '', acepto: false });
  const [verClave, setVerClave] = useState(false);
  const [errores, setErrores] = useState({});
  const [aviso, setAviso] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState('');
  const formRef = useRef(null);
  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); if (errores[k]) setErrores(e => ({ ...e, [k]: undefined })); };
  const prefijo = (PAISES_DEF.find(([p]) => p === f.pais) || [])[1] || '';
  const esColombia = f.pais === 'Colombia';
  const esMedicina = f.profesion === 'Medicina';
  const nivel = nivelClave(f.clave);
  // Celular obligatorio (lo usan los avisos del curso); el país da el indicativo.
  const telObligatorio = config.telefono_obligatorio !== false;

  const validar = () => {
    const e = {};
    if (f.nombre.trim().length < 2) e.nombre = 'Escribe tu nombre.';
    if (f.apellido.trim().length < 2) e.apellido = 'Escribe tu apellido.';
    if (f.documento && !/^[0-9A-Za-z.-]{3,20}$/.test(f.documento.trim())) e.documento = 'Solo números y letras, sin espacios.';
    if (telObligatorio && !f.pais) e.pais = 'Elige tu país.';
    if (telObligatorio && !f.telefono.trim()) e.telefono = 'Escribe tu celular: te avisaremos de tus cursos.';
    else if (f.telefono && !/^[0-9\s-]{6,15}$/.test(f.telefono.trim())) e.telefono = 'Escribe solo el número, sin el indicativo.';
    if (!f.institucion) e.institucion = 'Elige tu institución (o «Sin institución»).';
    if (esMedicina && !f.especialidad) e.especialidad = 'Elige tu especialidad.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = 'Escribe un correo válido, por ejemplo nombre@correo.com.';
    if (f.clave.length < 6) e.clave = 'Usa al menos 6 caracteres.';
    if (!f.acepto) e.acepto = 'Debes aceptar los términos para crear la cuenta.';
    return e;
  };

  const enviar = async (ev) => {
    ev.preventDefault();
    if (enviando) return;
    setAviso(null);
    const e = validar();
    setErrores(e);
    if (Object.keys(e).length) {
      formRef.current?.querySelector(`[name="${Object.keys(e)[0]}"]`)?.focus();
      return;
    }
    setEnviando(true);
    const extra = {
      apellido: f.apellido.trim(),
      institucion: f.institucion,
      profesion: f.profesion || undefined,
      ciudad: esColombia ? (f.ciudad || undefined) : undefined,
      acepta_terminos: `Acepto términos y condiciones y recibir información comercial (${new Date().toISOString().slice(0, 10)})`,
    };
    try {
      const res = await authService.register({
        nombre: `${f.nombre.trim()} ${f.apellido.trim()}`,
        email: f.email.trim(),
        password: f.clave,
        dni: f.documento.trim() || undefined,
        telefono: f.telefono.trim() || undefined,
        codigo_pais: f.telefono.trim() && prefijo ? prefijo : undefined,
        pais: f.pais || undefined,
        especialidad: esMedicina ? f.especialidad : (f.profesion || undefined),
        campos_personalizados: Object.fromEntries(Object.entries(extra).filter(([, v]) => v)),
      });
      setListo(f.nombre.trim().split(' ')[0]);
      setTimeout(() => { login({ token: res.data.token, user: res.data.user }); navigate(destino); }, 1100);
    } catch (err) {
      const d = err.response?.data || {};
      if (d.code === 'ya_registrado') {
        setAviso(<>Ya tienes una cuenta con este correo. <Link to="/login">Inicia sesión</Link> o recupera tu contraseña.</>);
      } else {
        setAviso(d.message || 'No pudimos crear la cuenta. Revisa tu conexión e inténtalo de nuevo.');
      }
      setEnviando(false);
    }
  };

  if (listo) {
    return (
      <Listo role="status">
        <div className="check"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg></div>
        <h2>¡Bienvenido, {listo}!</h2>
        <p>Tu cuenta está lista. Abriendo tus cursos…</p>
      </Listo>
    );
  }

  const campo = (k, etiqueta, entrada, { req, ayuda, clase } = {}) => (
    <Campo $error={!!errores[k]} className={clase}>
      <span>{etiqueta}{req && <i aria-hidden="true"> *</i>}</span>
      {entrada}
      {(errores[k] || ayuda) && <small id={`ayuda-${k}`}>{errores[k] || ayuda}</small>}
    </Campo>
  );
  const attrs = (k) => ({ name: k, value: f[k], onChange: e => set(k, e.target.value), 'aria-invalid': !!errores[k], 'aria-describedby': errores[k] ? `ayuda-${k}` : undefined });

  return (
    <Form ref={formRef} onSubmit={enviar} noValidate>
      <h2 id="acceso-titulo">{config.titulo_registro || 'Crea tu cuenta'}</h2>
      <p>{config.texto_registro || 'Te toma un minuto. Los campos con * son obligatorios.'}</p>

      <fieldset>
        <legend><b>1</b>Tus datos</legend>
        <Grilla>
          {campo('nombre', 'Nombre', <div className="caja"><input {...attrs('nombre')} autoComplete="given-name" /></div>, { req: true })}
          {campo('apellido', 'Apellido', <div className="caja"><input {...attrs('apellido')} autoComplete="family-name" /></div>, { req: true })}
          {campo('documento', 'Documento de identidad', <div className="caja"><input {...attrs('documento')} inputMode="numeric" /></div>)}
          {campo('pais', 'País', (
            <div className="caja"><select {...attrs('pais')} onChange={e => { set('pais', e.target.value); if (e.target.value !== 'Colombia') set('ciudad', ''); }}>
              <option value="">Elige un país</option>
              {PAISES_DEF.map(([p]) => <option key={p} value={p}>{p}</option>)}
            </select></div>
          ), { req: telObligatorio })}
          {esColombia && campo('ciudad', 'Ciudad', (
            <div className="caja"><select {...attrs('ciudad')}><option value="">Elige tu ciudad</option>{listas.ciudades.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          ), { clase: 'aparece' })}
          {campo('telefono', 'Celular', (
            <div className="caja">{prefijo && <span className="prefijo">{prefijo}</span>}<input {...attrs('telefono')} type="tel" inputMode="tel" autoComplete="tel-national" placeholder={prefijo ? '' : 'Elige primero tu país'} /></div>
          ), { clase: esColombia ? 'ancho' : undefined, req: telObligatorio })}
        </Grilla>
      </fieldset>

      <fieldset>
        <legend><b>2</b>Tu perfil</legend>
        <Grilla>
          {campo('institucion', 'Institución', (
            <div className="caja"><select {...attrs('institucion')}><option value="">Elige una</option>{listas.instituciones.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          ), { req: true })}
          {campo('profesion', 'Profesión', (
            <div className="caja"><select {...attrs('profesion')} onChange={e => { set('profesion', e.target.value); if (e.target.value !== 'Medicina') set('especialidad', ''); }}>
              <option value="">Elige una</option>{listas.profesiones.map(c => <option key={c} value={c}>{c}</option>)}
            </select></div>
          ))}
          {esMedicina && campo('especialidad', 'Especialidad', (
            <div className="caja"><select {...attrs('especialidad')}><option value="">Elige tu especialidad</option>{listas.especialidades.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          ), { req: true, clase: 'ancho aparece' })}
        </Grilla>
      </fieldset>

      <fieldset>
        <legend><b>3</b>Tu cuenta</legend>
        <Grilla>
          {campo('email', 'Correo electrónico', <div className="caja"><input {...attrs('email')} type="email" inputMode="email" autoComplete="email" placeholder="tu@correo.com" /></div>, { req: true, clase: 'ancho' })}
          {campo('clave', 'Contraseña', (
            <>
              <div className="caja">
                <input {...attrs('clave')} type={verClave ? 'text' : 'password'} autoComplete="new-password" />
                <button type="button" className="ojo" onClick={() => setVerClave(v => !v)} aria-pressed={verClave} aria-label={verClave ? 'Ocultar contraseña' : 'Mostrar contraseña'}><Ojo tachado={verClave} /></button>
              </div>
              {f.clave && <Fuerza $nivel={nivel} aria-hidden="true">{[1, 2, 3].map(n => <i key={n} className={nivel >= n ? 'on' : ''} />)}</Fuerza>}
            </>
          ), { req: true, clase: 'ancho', ayuda: f.clave ? (nivel >= 3 ? 'Contraseña segura.' : nivel === 2 ? 'Bien. Con números o mayúsculas queda más segura.' : 'Muy corta: mínimo 6 caracteres.') : 'Mínimo 6 caracteres.' })}
        </Grilla>
      </fieldset>

      <Acepto $error={!!errores.acepto}>
        <input type="checkbox" name="acepto" checked={f.acepto} onChange={e => set('acepto', e.target.checked)} aria-invalid={!!errores.acepto} />
        <span>
          Acepto los <Link to="/terminos-de-uso" target="_blank">términos y condiciones</Link> y la <Link to="/politica-de-privacidad" target="_blank">política de privacidad</Link>, y recibir información comercial.
          {errores.acepto && <small style={{ display: 'block', marginTop: 4, color: '#ffb3b3' }}>{errores.acepto}</small>}
        </span>
      </Acepto>

      {aviso && <Error role="alert" style={{ marginTop: '1rem' }}>{aviso}</Error>}
      <Enviar type="submit" disabled={enviando}>{enviando ? <><span className="giro" aria-hidden="true" />Creando tu cuenta…</> : 'Crear cuenta'}</Enviar>
      <Pie>¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link></Pie>
    </Form>
  );
};

export default RegistroStreaming;
